import React, { useEffect, useRef } from 'react';
import { createChart, CrosshairMode, LineStyle } from 'lightweight-charts';

// Mirror of the app's CSS variables — used for TradingView canvas which can't
// read CSS custom properties directly.
const THEME = {
  bg:      '#0d1117',
  surface: '#161b22',
  border:  '#30363d',
  text:    '#e6edf3',
  muted:   '#8b949e',
};

const INDICATOR_COLORS = ['#d29922', '#00d4aa', '#ec4899', '#ad93e9', '#06b6d4', '#f85149'];

const EXTERNAL_DATA_LABELS = new Set([
  'VIX (Volatility Index)', 'Gold (GLD ETF)', 'US Dollar Index (UUP ETF)',
  '10-Year Treasury Yield', '2-Year Treasury Yield', '10Y-2Y Treasury Spread',
  'Federal Funds Rate', 'Unemployment Rate', 'Consumer Sentiment',
]);

/** Convert an ISO date string (daily or intraday) to a Unix timestamp in seconds. */
function toUnixSec(dateStr) {
  if (!dateStr) return 0;
  return dateStr.includes('T')
    ? Math.floor(new Date(dateStr + 'Z').getTime() / 1000)
    : Math.floor(new Date(dateStr + 'T00:00:00Z').getTime() / 1000);
}

const RANGE_BUTTONS = [
  { label: '1W', months: 0.25 },
  { label: '1M', months: 1 },
  { label: '3M', months: 3 },
  { label: '6M', months: 6 },
  { label: 'All', months: null },
];

/**
 * TradingView Lightweight Charts price chart.
 *
 * Props:
 *   data            – OHLCV rows with indicator values merged in (same shape as
 *                     priceChartData from BacktestResults).
 *   indicatorNames  – string[]  names of indicators present in data rows.
 *   tradeEvents     – { date, action: 'BUY'|'SELL'|'SHORT'|'COVER' }[]
 *   isFx            – boolean   true for forex (5dp, no $ prefix)
 *   onVisibleRangeChange – ({ startDate, endDate }) => void  for equity curve sync
 *
 * The parent should set key={backtest.id} so the component is remounted (and the
 * chart fully recreated) when the backtest changes. This avoids complex effect
 * dependency management for the TradingView imperative API.
 */
export default function PriceChart({ data, indicatorNames, tradeEvents, isFx, onVisibleRangeChange, onCrosshairMove }) {
  const containerRef = useRef(null);
  const chartRef = useRef(null);
  const callbackRef = useRef(onVisibleRangeChange);
  useEffect(() => { callbackRef.current = onVisibleRangeChange; });
  const crosshairRef = useRef(onCrosshairMove);
  useEffect(() => { crosshairRef.current = onCrosshairMove; });

  // Chart is created once on mount (parent controls remounting via key prop).
  useEffect(() => {
    const container = containerRef.current;
    if (!container || !data || data.length === 0) return;

    const isIntraday = !!data[0]?.date?.includes('T');
    const isExternal   = (n) => EXTERNAL_DATA_LABELS.has(n) || n.startsWith('Corr(');
    // Oscillators live on a different scale (0-100 or centred at 0) and must NOT
    // share the right price-scale with candlesticks — doing so collapses FX price
    // movement to a flat line because the y-axis expands to 0-100.
    const isOscillator = (n) => /^(RSI|Stoch|MACD)/i.test(n);
    const priceScaleInds = (indicatorNames || []).filter((n) => !isExternal(n));
    const externalInds   = (indicatorNames || []).filter((n) =>  isExternal(n));
    const overlayInds    = priceScaleInds.filter((n) => !isOscillator(n));
    const oscillatorInds = priceScaleInds.filter((n) =>  isOscillator(n));
    const hasOscillators = oscillatorInds.length > 0;

    // ── Create chart ────────────────────────────────────────────────────────
    const chart = createChart(container, {
      width:  container.clientWidth,
      height: 420,
      layout: {
        background: { type: 'solid', color: THEME.bg },
        textColor:  THEME.text,
        fontSize:   11,
        fontFamily: "'Oxanium', sans-serif",
      },
      grid: {
        vertLines: { color: THEME.border },
        horzLines: { color: THEME.border },
      },
      crosshair: { mode: CrosshairMode.Normal },
      rightPriceScale: {
        borderColor:  THEME.border,
        // When oscillators are present they occupy the lower ~35% of the chart;
        // compress the price area into the upper ~60% to leave room for them.
        scaleMargins: hasOscillators
          ? { top: 0.04, bottom: 0.40 }
          : { top: 0.06, bottom: 0.22 },
      },
      timeScale: {
        borderColor:    THEME.border,
        timeVisible:    isIntraday,
        secondsVisible: false,
        barSpacing:     6,
        minBarSpacing:  1,
      },
    });
    chartRef.current = chart;

    // ── Candlestick series ──────────────────────────────────────────────────
    const candleSeries = chart.addCandlestickSeries({
      upColor:        '#26a641',
      downColor:      '#f85149',
      borderUpColor:  '#26a641',
      borderDownColor:'#f85149',
      wickUpColor:    '#26a641',
      wickDownColor:  '#f85149',
      priceLineVisible: false,
      title: '',
      // FX pairs quote to 5 decimal places (pip = 0.00001)
      priceFormat: isFx
        ? { type: 'price', precision: 5, minMove: 0.00001 }
        : { type: 'price', precision: 2, minMove: 0.01 },
    });
    candleSeries.setData(
      data.map((d) => ({
        time:  toUnixSec(d.date),
        open:  d.open,
        high:  d.high,
        low:   d.low,
        close: d.close,
      }))
    );

    // ── Volume histogram ────────────────────────────────────────────────────
    const hasVolume = data.some((d) => d.volume > 0);
    let volumeSeries = null;
    if (hasVolume) {
      volumeSeries = chart.addHistogramSeries({
        priceFormat:  { type: 'volume' },
        priceScaleId: 'volume',
      });
      chart.priceScale('volume').applyOptions({
        // Keep volume bars inside the price pane (not overlapping oscillators)
        scaleMargins: hasOscillators
          ? { top: 0.50, bottom: 0.40 }
          : { top: 0.80, bottom: 0.00 },
      });
      volumeSeries.setData(
        data.map((d) => ({
          time:  toUnixSec(d.date),
          value: d.volume || 0,
          color: d.close >= d.open
            ? 'rgba(38,166,65,0.3)'
            : 'rgba(248,81,73,0.3)',
        }))
      );
    }

    // ── Overlay indicator lines (SMA, EMA, Bollinger — same scale as price) ──
    overlayInds.forEach((name, i) => {
      const s = chart.addLineSeries({
        color:            INDICATOR_COLORS[i % INDICATOR_COLORS.length],
        lineWidth:        1.5,
        priceLineVisible: false,
        lastValueVisible: true,
        title:            name,
      });
      s.setData(
        data
          .filter((d) => d[name] != null)
          .map((d) => ({ time: toUnixSec(d.date), value: d[name] }))
      );
    });

    // ── Oscillator lines (RSI, Stochastic, MACD — own price scale) ─────────
    // Each oscillator shares a dedicated 'oscillator' scale in the lower band
    // of the chart so they never distort the candlestick y-axis.
    oscillatorInds.forEach((name, i) => {
      const s = chart.addLineSeries({
        color:            INDICATOR_COLORS[(overlayInds.length + i) % INDICATOR_COLORS.length],
        lineWidth:        1.5,
        priceLineVisible: false,
        lastValueVisible: true,
        title:            name,
        priceScaleId:     'oscillator',
      });
      s.setData(
        data
          .filter((d) => d[name] != null)
          .map((d) => ({ time: toUnixSec(d.date), value: d[name] }))
      );
    });
    if (hasOscillators) {
      chart.priceScale('oscillator').applyOptions({
        borderColor:  THEME.border,
        scaleMargins: { top: 0.64, bottom: 0.02 },
      });
    }

    // ── External indicators (own price scales on right) ─────────────────────
    externalInds.forEach((name, i) => {
      const s = chart.addLineSeries({
        color:            INDICATOR_COLORS[(priceScaleInds.length + i) % INDICATOR_COLORS.length],
        lineWidth:        1.5,
        priceLineVisible: false,
        lastValueVisible: true,
        title:            name,
        priceScaleId:     `ext-${i}`,
        lineStyle:        LineStyle.Dashed,
      });
      s.setData(
        data
          .filter((d) => d[name] != null)
          .map((d) => ({ time: toUnixSec(d.date), value: d[name] }))
      );
    });

    // ── Trade markers ───────────────────────────────────────────────────────
    // Snap every trade to its nearest chart bar, then group by bar so that
    // downsampled charts show at most one BUY + one SELL marker per bar.
    // Multiple trades of the same type are shown as a single marker with ×N.
    const barTimes = data.map((d) => toUnixSec(d.date));
    const snapToBar = (tradeTs) => {
      let lo = 0, hi = barTimes.length - 1, idx = 0;
      while (lo <= hi) {
        const mid = (lo + hi) >> 1;
        if (barTimes[mid] <= tradeTs) { idx = mid; lo = mid + 1; }
        else hi = mid - 1;
      }
      return barTimes[idx];
    };
    const groupedByBar = {};
    (tradeEvents || []).forEach((t) => {
      const barTime = snapToBar(toUnixSec(t.date));
      if (!groupedByBar[barTime]) groupedByBar[barTime] = { BUY: 0, SELL: 0, SHORT: 0, COVER: 0 };
      groupedByBar[barTime][t.action] = (groupedByBar[barTime][t.action] || 0) + 1;
    });
    // BUY/SELL are the long lifecycle (green entry, red exit); SHORT/COVER are the
    // short lifecycle (orange entry, teal exit) so the two are never confusable even
    // though SHORT and SELL are both "down" signals and BUY/COVER are both "up" fills.
    const markersData = Object.entries(groupedByBar)
      .flatMap(([barTimeStr, counts]) => {
        const time = Number(barTimeStr);
        const markers = [];
        if (counts.BUY   > 0) markers.push({ time, position: 'belowBar', color: '#26a641', shape: 'arrowUp',   text: counts.BUY   > 1 ? `×${counts.BUY}`   : '', size: 1 });
        if (counts.SELL  > 0) markers.push({ time, position: 'aboveBar', color: '#f85149', shape: 'arrowDown', text: counts.SELL  > 1 ? `×${counts.SELL}`  : '', size: 1 });
        if (counts.SHORT > 0) markers.push({ time, position: 'belowBar', color: '#d29922', shape: 'arrowDown', text: counts.SHORT > 1 ? `×${counts.SHORT}` : '', size: 1 });
        if (counts.COVER > 0) markers.push({ time, position: 'aboveBar', color: '#00d4aa', shape: 'arrowUp',   text: counts.COVER > 1 ? `×${counts.COVER}` : '', size: 1 });
        return markers;
      })
      .sort((a, b) => a.time - b.time);
    if (markersData.length > 0) candleSeries.setMarkers(markersData);

    // lightweight-charts doesn't auto-fit all loaded bars to the container on
    // mount — with fixed bar spacing it only shows however many recent bars fit
    // the width, silently hiding older data until the user manually zooms out.
    // container.clientWidth can still be 0/stale at this exact synchronous
    // point (layout hasn't settled yet, esp. on mobile with banners/reflow),
    // so re-apply the real width and re-fit after the browser's next paint —
    // requestAnimationFrame is a hard guarantee of "after layout", unlike
    // ResizeObserver whose callback timing varies by platform.
    chart.timeScale().fitContent();
    const fitAfterPaint = () => {
      if (!chartRef.current) return; // unmounted before the frame fired
      chart.applyOptions({ width: container.clientWidth });
      chart.timeScale().fitContent();
    };
    const rafId1 = requestAnimationFrame(() => {
      fitAfterPaint();
      // A second frame covers layouts that shift again just after the first
      // paint (e.g. a banner/reflow settling in on mobile).
      requestAnimationFrame(fitAfterPaint);
    });

    // ── Crosshair sync to equity curve (rAF-throttled, ~60fps cap) ────────────
    let crosshairRafId = null;
    chart.subscribeCrosshairMove((param) => {
      if (crosshairRafId !== null) return;
      crosshairRafId = requestAnimationFrame(() => {
        crosshairRafId = null;
        if (!crosshairRef.current) return;
        if (!param?.time) { crosshairRef.current(null); return; }
        const d = new Date(param.time * 1000);
        const iso = isIntraday
          ? d.toISOString().slice(0, 19)
          : d.toISOString().slice(0, 10);
        crosshairRef.current(iso);
      });
    });

    // ── Equity curve sync via visible time range (debounced 200ms) ──────────
    let syncTimer = null;
    chart.timeScale().subscribeVisibleTimeRangeChange((range) => {
      if (!range) return;
      clearTimeout(syncTimer);
      syncTimer = setTimeout(() => {
        if (callbackRef.current) {
          const fromDate = new Date(range.from * 1000).toISOString().slice(0, 19);
          const toDate   = new Date(range.to   * 1000).toISOString().slice(0, 19);
          callbackRef.current({ startDate: fromDate, endDate: toDate });
        }
      }, 200);
    });

    // ── Responsive resize ───────────────────────────────────────────────────
    // Ongoing window/container resizes after mount just keep the chart's pixel
    // width in sync — deliberately does NOT re-fit, so it doesn't fight the
    // user's manual zoom/pan once they've interacted with the chart.
    const ro = new ResizeObserver((entries) => {
      if (entries[0]) chart.applyOptions({ width: entries[0].contentRect.width });
    });
    ro.observe(container);

    return () => {
      cancelAnimationFrame(rafId1);
      if (crosshairRafId !== null) cancelAnimationFrame(crosshairRafId);
      clearTimeout(syncTimer);
      ro.disconnect();
      chart.remove();
      chartRef.current = null;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  // Empty deps: component is remounted via key={backtest.id} in the parent whenever
  // the backtest changes, so this runs exactly once per chart instance.

  // ── Time-range quick-select buttons ────────────────────────────────────────
  const setRange = (months) => {
    const chart = chartRef.current;
    if (!chart || !data || !data.length) return;
    if (months === null) {
      chart.timeScale().fitContent();
    } else {
      const toTime   = toUnixSec(data[data.length - 1].date);
      const fromTime = toTime - Math.round(months * 30.44 * 24 * 3600);
      chart.timeScale().setVisibleRange({ from: fromTime, to: toTime });
    }
  };

  return (
    <div style={{ position: 'relative' }}>
      {/* Range buttons */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 6 }}>
        {RANGE_BUTTONS.map(({ label, months }) => (
          <button
            key={label}
            onClick={() => setRange(months)}
            style={{
              padding: '2px 8px', fontSize: 11, fontWeight: 600,
              background: 'var(--surface)', border: '1px solid var(--border)',
              borderRadius: 4, cursor: 'pointer', color: 'var(--muted)',
              transition: 'color 0.15s',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--text)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--muted)'; }}
          >
            {label}
          </button>
        ))}
      </div>
      {/* Chart canvas */}
      <div ref={containerRef} style={{ width: '100%' }} />
    </div>
  );
}
